import React, { useState } from 'react';
import AnatomyModal from './AnatomyModal';
import CartBanner from './CartBanner';

// A category needs the anatomy modal only if it has both V- and P-prefixed
// services (i.e. there's a meaningful choice between menus).
export function categoryNeedsAnatomy(services) {
  if (!services?.length) return false;
  let hasV = false, hasP = false;
  for (const s of services) {
    const code = (s.code || '').toUpperCase();
    if (code.startsWith('V-')) hasV = true;
    else if (code.startsWith('P-')) hasP = true;
    if (hasV && hasP) return true;
  }
  return false;
}

const BADGE_LABELS = {
  most_booked: { glyph: '★', text: 'Most booked', className: 'category-row-badge category-row-badge--most-booked' },
  trending:    { glyph: '↗', text: 'Trending',    className: 'category-row-badge category-row-badge--trending' },
};

// Convert SHOUTED Zenoti category names to title case. Punctuation-only
// tokens like "&" pass through. Used only as a fallback when the admin
// hasn't set a displayName override.
function titleCaseFallback(str) {
  if (!str) return '';
  return str
    .split(' ')
    .map(word => {
      if (!word) return word;
      if (/^[^a-zA-Z0-9]+$/.test(word)) return word; // pure punctuation, e.g. "&"
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function CategoryGridStep({ selectedCenter, categoriesData, allCenterServices, onCategoryTap, anatomy, onAnatomySet, selectedServices, onToggleService }) {
  const [pendingTile, setPendingTile] = useState(null);

  // Derive rows from the (already pre-sorted by displayOrder) Zenoti category list.
  const rows = (categoriesData || [])
    .map(zenotiCat => {
      const services = allCenterServices?.services?.[zenotiCat.id];
      if (!services || services.length === 0) return null;
      // displayName (admin override) renders verbatim; Zenoti name gets
      // title-cased so SHOUTED entries like "BROW & LASH" don't dominate.
      const title = zenotiCat.displayName
        ? zenotiCat.displayName
        : titleCaseFallback(zenotiCat.name || '');
      return {
        cat: { id: zenotiCat.id, name: zenotiCat.name, needsAnatomy: categoryNeedsAnatomy(services) },
        zenotiCat,
        title,
        copy: zenotiCat.displayCopy || null,
        imageUrl: zenotiCat.imageUrl || null,
        badge: zenotiCat.badge || null,
      };
    })
    .filter(Boolean);

  function handleTileClick(cat, zenotiCat) {
    if (cat.needsAnatomy && !anatomy) {
      setPendingTile({ cat, zenotiCat });
      return;
    }
    onCategoryTap(cat, zenotiCat);
  }

  function handleAnatomyChoice(value) {
    onAnatomySet?.(value);
    if (pendingTile) {
      const { cat, zenotiCat } = pendingTile;
      setPendingTile(null);
      onCategoryTap(cat, zenotiCat);
    }
  }

  return (
    <div className="step-content">
      <CartBanner
        selectedServices={selectedServices}
        onToggleService={onToggleService}
      />

      <p className="category-eyebrow">Choose your menu</p>
      <h1 className="category-page-title">What brings you in?</h1>
      {selectedCenter?.name && (
        <p className="category-page-subtitle">at {selectedCenter.name}</p>
      )}

      <div className="category-row-list">
        {rows.map(({ cat, zenotiCat, title, copy, imageUrl, badge }) => {
          const badgeMeta = badge ? BADGE_LABELS[badge] : null;
          return (
            <button
              key={cat.id}
              type="button"
              className="category-row"
              onClick={() => handleTileClick(cat, zenotiCat)}
              aria-label={title}
            >
              <span className="category-row-thumb" aria-hidden="true">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <span className="category-row-thumb-placeholder" />
                )}
              </span>

              <span className="category-row-text">
                <span className="category-row-title-line">
                  <span className="category-row-title">{title}</span>
                  {badgeMeta && (
                    <span className={badgeMeta.className}>
                      <span aria-hidden="true">{badgeMeta.glyph}</span> {badgeMeta.text}
                    </span>
                  )}
                </span>
                {copy && <span className="category-row-copy">{copy}</span>}
              </span>

              <span className="category-row-chevron" aria-hidden="true">›</span>
            </button>
          );
        })}
      </div>

      <AnatomyModal open={!!pendingTile} onChoose={handleAnatomyChoice} />
    </div>
  );
}

export default CategoryGridStep;
